import React, { useState } from 'react';
import Autosuggest from 'react-autosuggest';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Navbar, { Brand } from 'react-bootstrap/lib/Navbar';
import DropdownButton from 'react-bootstrap/lib/DropdownButton';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import fileSaver from 'file-saver';
import moment from 'moment';

import { search } from '../../containers/settingsSlice';
import ConfirmationMessage from '../../../Controls/ConfirmationMessage';

import generalNavBarStyles from '../../../common/css/NavBar.module.css';
import settingsNavBarStyles from './NavBar.module.css';
import {
    APPLICATION_SETTINGS_FIELDS,
    AZURE_CONNECTORS_FIELDS,
    COMPANY_INFO_FIELDS,
    PROTECTED_DOMAIN_FIELDS,
    SECURITY_MAIN_FIELDS
} from '../../common/fields';

import settingsIcon from '../../../common/Images/settings_icon.png';
import searchIcon from '../../common/Images/SearchIcon.png';
import restartIcon from '../../../common/Images/refresh_black.png';
import ImportFromFile from '../../../../components/Controls/ImportFromFile';
import { httpGet, httpPost } from '../../../utils';
import useFeature from '../../hooks/featureFlags/useFeature';
import { FEATURE_FLAGS_NAMES, LAYOUT_TABS } from '@silverfort-nodelibs/common/utils/consts';
import { SYSTEM_MODE_HIDDEN_VIEWS, SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { isFeatureActive } from '@silverfort-nodelibs/common/utils/common';
import { useGetVisibleCategoryIds } from '../../hooks/lockedFeatures/useCategoryDisplay';
import { useGetTabsTermsToHide } from '../../hooks/lockedFeatures/useTabDisplay';
import { useGetFieldsTermsToHide } from '../../hooks/lockedFeatures/useFieldDisplay';
import { IconButton, Icon, Icons, Tooltip } from '@silverfort/sf-components';
import { useAppDrawer } from '../../../AppDrawer/AppDrawerProvider';
import ReportsDrawer from '../../../ReportsDrawer/ReportsDrawer';
import { useIsCloudOnly } from '../../../SystemSettings/hooks/featureFlags/useIsCloudOnly';

const IGNORED_SEARCH_TERMS = [
    SECURITY_MAIN_FIELDS.CERTIFICATE_REQUEST.NAME,
    APPLICATION_SETTINGS_FIELDS.DEFAULT_CERTIFICATE_REQUEST.NAME,
    PROTECTED_DOMAIN_FIELDS.API_CERTIFICATE_REQUEST.NAME,
    COMPANY_INFO_FIELDS.AUTO_DENY_MFA_THRESHOLD.NAME,
    COMPANY_INFO_FIELDS.AUTO_DENY_MFA_WINDOW_SIZE.NAME
];

const SettingsNavBar = ({ setIsDuringSave, setDuringAction, isEditor }) => {
    const history = useHistory();

    const searchQuery = useSelector((state) => state.settings.searchQuery);
    const categories = useSelector((state) => state.settings.categories);
    const [suggestions, setSuggestions] = useState([]);
    const [showImportModal, setShowImportModal] = useState(false);
    const [isImportSettingsError, setIsImportSettingsError] = useState(false);
    const [importSettingsErrorText, setImportSettingsErrorText] = useState('');
    const [showRestartModal, setShowRestartModal] = useState(false);
    const newADConnectors = useFeature(FEATURE_FLAGS_NAMES.NewADConnectors);
    const visibleCategoryIds = useGetVisibleCategoryIds();
    const tabTermsToHide = useGetTabsTermsToHide();
    const fieldsTermsToHide = useGetFieldsTermsToHide();
    const { displayContent } = useAppDrawer();
    const isCloudOnly = useIsCloudOnly();

    const dispatch = useDispatch();
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);

    const onClear = () => {
        dispatch(search(''));
    };

    const importApproveHandler = async (importFileContent) => {
        try {
            setShowImportModal(false);
            setIsDuringSave(true);
            await httpPost('/Settings/ImportSystemConfiguration', { configurations: importFileContent });
            setTimeout(() => {
                window.location.reload();
            }, 5000);
        } catch (err) {
            setIsDuringSave(false);
            if (err.response && err.response.data && err.response.data.error) {
                setImportSettingsErrorText(err.response.data.error);
                setIsImportSettingsError(true);
                setTimeout(() => {
                    setImportSettingsErrorText('');
                    setIsImportSettingsError(false);
                }, 5000);
            } else {
                console.error(err);
            }
        }
    };
    const exportSettings = async () => {
        try {
            const result = await httpGet('/Settings/GetSystemConfiguration');
            const configurationString = JSON.stringify(result.data.SystemConfiguration);
            const blob = new Blob([configurationString], { type: 'application/json' });
            fileSaver.saveAs(blob, `SilverfortConfiguration_${moment().local().format()}.json`);
        } catch (err) {
            console.error(err);
        }
    };

    const restartApproveHandler = async () => {
        try {
            setShowImportModal(false);
            setDuringAction(true);
            await httpPost('/Settings/restartServer', { restart: true });
            setTimeout(() => {
                window.location.href = '/Login';
            }, 5000);
        } catch (err) {
            setDuringAction(false);
            console.error(err);
        }
    };

    const onSearchQueryChange = (event, { newValue }) => {
        dispatch(search(newValue));
        if (newValue === '') {
            setSuggestions([]);
        }
    };

    const getCategoriesNamesBySystemMode = (categories) => {
        const filteredCategories = [];
        for (const categoryId in categories) {
            if (visibleCategoryIds.includes(categoryId)) filteredCategories.push(categoryId);
        }

        return filteredCategories;
    };

    const filterTermsBySystemMode = (terms) => {
        return terms.filter(
            (term) =>
                !SYSTEM_MODE_HIDDEN_VIEWS[LAYOUT_TABS.SYSTEM_SETUP.ID][systemMode].terms.has(term) &&
                !tabTermsToHide.includes(term) &&
                !fieldsTermsToHide.includes(term)
        );
    };

    const getSuggestions = (query) => {
        const escapedQuery = query.value.trim().toLowerCase();

        if (escapedQuery === '') {
            return [];
        }
        const termsSet = new Set();

        const newSuggestions = [];
        const featureFlagsIgnore = new Set();
        if (isFeatureActive(newADConnectors)) {
            featureFlagsIgnore.add(AZURE_CONNECTORS_FIELDS.AZURE_REDIRECT_URI.NAME);
        }

        const filteredCategoriesNames = getCategoriesNamesBySystemMode(categories);
        for (const category of filteredCategoriesNames) {
            const filteredTerms = filterTermsBySystemMode(categories[category].terms);
            for (const term of filteredTerms) {
                const lowerTerm = term.toLowerCase();
                const fullSearchTerm = `${categories[category].name}__${lowerTerm}`;
                if (
                    lowerTerm.includes(escapedQuery) &&
                    !termsSet.has(fullSearchTerm) &&
                    !IGNORED_SEARCH_TERMS.includes(term) &&
                    !featureFlagsIgnore.has(term)
                ) {
                    newSuggestions.push({ category: categories[category].name, foundTerm: term });
                    termsSet.add(fullSearchTerm);
                }
            }
        }

        setSuggestions(newSuggestions);
    };

    const getSuggestionValue = (suggestion) => {
        return suggestion.foundTerm;
    };

    const renderSuggestion = (suggestion) => {
        return (
            <span
                className={settingsNavBarStyles.suggestionContent}
                title={`${suggestion.category} - ${suggestion.foundTerm}`}>
                <div>
                    {suggestion.category}
                    {'  -  '}
                </div>

                <div className={settingsNavBarStyles.suggestionFoundTerm}>{suggestion.foundTerm}</div>
            </span>
        );
    };
    const onSuggestionsClearRequested = () => {
        setSuggestions([]);
    };

    const onSuggestionSelected = (event, { suggestion }) => {
        history.push(`/Settings/${suggestion.category}`);
    };

    const inputProps = {
        placeholder: 'Search...',
        value: searchQuery,
        onChange: onSearchQueryChange
    };

    const openReportsDrawer = () => {
        displayContent(<ReportsDrawer />);
    };

    return (
        <>
            <Navbar fluid={true} style={{ margin: 0 }}>
                <div className={generalNavBarStyles.navbarContent}>
                    <div className={generalNavBarStyles.navbarLefSide}>
                        <Brand>
                            <img src={settingsIcon} alt="" />
                            <span>System Settings</span>
                        </Brand>
                    </div>
                    <div className={`${generalNavBarStyles.navbarMiddleLine} ${settingsNavBarStyles.searchContainer}`}>
                        <div className={settingsNavBarStyles.autosuggestContainer}>
                            <Autosuggest
                                suggestions={suggestions}
                                onSuggestionsFetchRequested={getSuggestions}
                                getSuggestionValue={getSuggestionValue}
                                renderSuggestion={renderSuggestion}
                                onSuggestionsClearRequested={onSuggestionsClearRequested}
                                onSuggestionSelected={onSuggestionSelected}
                                inputProps={inputProps}
                                alwaysRenderSuggestions={false}
                            />
                        </div>

                        {!searchQuery && (
                            <span className={settingsNavBarStyles.searchBtn}>
                                <img src={searchIcon} alt="" />
                            </span>
                        )}

                        {searchQuery && (
                            <span className={settingsNavBarStyles.clearBtn} onClick={onClear}>
                                x
                            </span>
                        )}
                    </div>
                    <div className={generalNavBarStyles.navbarRightSide}>
                        {systemMode === SYSTEM_MODES.ON_PREM && (
                            <>
                                {isImportSettingsError && (
                                    <span className={generalNavBarStyles.error}>{importSettingsErrorText}</span>
                                )}
                                <DropdownButton
                                    id={generalNavBarStyles.navbarOptionsDropdown}
                                    pullRight
                                    noCaret
                                    title={
                                        <Icon
                                            icon={Icons.Settings}
                                            size={18}
                                            className={`${settingsNavBarStyles.navbarOptionsDropdownBtn} ${generalNavBarStyles.navbarOptionsDropdownBtn} text-white`}
                                        />
                                    }>
                                    <MenuItem disabled={!isEditor} onClick={() => setShowImportModal(true)}>
                                        <i className={`fa fa-upload ${settingsNavBarStyles.menuItemIcon}`} />
                                        Import Configuration
                                    </MenuItem>
                                    <MenuItem disabled={!isEditor} onClick={exportSettings}>
                                        <i className={`fa fa-download ${settingsNavBarStyles.menuItemIcon}`} />
                                        Export Configuration
                                    </MenuItem>
                                    <MenuItem disabled={!isEditor} onClick={() => setShowRestartModal(true)}>
                                        <img src={restartIcon} className={settingsNavBarStyles.menuItemIcon} alt="" />
                                        Restart Server
                                    </MenuItem>
                                </DropdownButton>
                            </>
                        )}
                        {!isCloudOnly && (
                            <div className="pt-1.5">
                                <Tooltip title={'Reports'}>
                                    <IconButton
                                        icon={Icons.DocEmpty}
                                        onClick={openReportsDrawer}
                                        iconClassName="text-sf-white"
                                        iconSize={18}
                                    />
                                </Tooltip>
                            </div>
                        )}
                    </div>
                </div>
            </Navbar>
            {showImportModal && (
                <ImportFromFile
                    cancelClicked={() => setShowImportModal(false)}
                    importClicked={importApproveHandler}
                    modalTitle={'System Configuration'}
                    importDescName={'configuration'}
                    importType={'JSON'}
                    showRestartMsg={true}
                />
            )}
            {showRestartModal && (
                <ConfirmationMessage
                    title={'Restart Server'}
                    body={'Are you sure you want to restart?'}
                    approveText={'Restart'}
                    approveStyle={'danger'}
                    OnCancel={() => setShowRestartModal(false)}
                    OnApprove={restartApproveHandler}
                />
            )}
        </>
    );
};

export default React.memo(SettingsNavBar);
